import { useState } from 'react';
import { Download } from 'lucide-react';
import { PreflightCopy } from '@signalvote/shared';
import { PendingButton } from '@/components/ui/pending-button';
import { useCampaignStore } from '@/stores';
import {
  useDownloadAuditPacket,
  AuditPacketDownloadError,
} from '@/hooks/queries/useFinanceReports';
import { triggerBrowserDownload } from './downloadHelpers';

/**
 * FPD-026 — "Download audit packet" button. Renders on filed-report detail
 * pages for users with `Finance.Manage`; null otherwise. Streams the
 * FPD-014 audit-packet ZIP into a synthetic `<a download>` click via
 * {@link triggerBrowserDownload}.
 *
 * Capability check is a UX nicety only — the server's
 * `[RequireCapability(Finance, Manage)]` attribute on the endpoint is the
 * real boundary. A 403 returned mid-session (e.g. capability revoked while
 * the page sat open) renders the AC literal copy in
 * {@link PreflightCopy.AuditPacketDownload403}.
 */
export function DownloadAuditPacketButton({ reportId }: { reportId: string }) {
  const canManage = useCampaignStore((s) => s.canManage('Finance'));
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const { downloadAuditPacket } = useDownloadAuditPacket();

  if (!canManage) return null;

  const onClick = async () => {
    setError(null);
    setDownloading(true);
    try {
      const result = await downloadAuditPacket(reportId);
      triggerBrowserDownload(result.zipBlob, result.fileName);
    } catch (e) {
      setError(resolveErrorCopy(e));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex flex-col gap-1" data-testid="audit-packet-download-section">
      <PendingButton
        type="button"
        variant="outline"
        size="sm"
        pending={downloading}
        icon={<Download className="h-4 w-4" />}
        onClick={onClick}
        data-testid="audit-packet-download-button"
      >
        {downloading
          ? PreflightCopy.AuditPacketDownloadPreparingLabel
          : PreflightCopy.AuditPacketDownloadButtonLabel}
      </PendingButton>
      {error && (
        <p
          className="text-sm text-destructive"
          role="alert"
          data-testid="audit-packet-download-error"
        >
          {error}
        </p>
      )}
    </div>
  );
}

function resolveErrorCopy(err: unknown): string {
  if (err instanceof AuditPacketDownloadError) {
    if (err.kind === 'Forbidden') return PreflightCopy.AuditPacketDownload403;
    if (err.kind === 'NotFound') return PreflightCopy.AuditPacketDownload404;
    return PreflightCopy.AuditPacketDownloadServerError;
  }
  // Network errors / unexpected throws fall through to the 5xx copy so the
  // treasurer sees a retryable affordance, not a stack trace.
  return PreflightCopy.AuditPacketDownloadServerError;
}
